import { useEffect, useMemo, useRef, useState } from "react";
import { useReducedMotion } from "framer-motion";
import { featured, categories } from "@/data/portfolio";
import PortfolioImage from "./PortfolioImage";

const feedFor = category => featured.filter(p => p.category === category).flatMap(project =>
  project.images.map((src, index) => ({ src, title: project.title, year: project.year, index }))
);

export default function Gallery() {
  const [active, setActive] = useState(categories[0]);
  const [playing, setPlaying] = useState(true);
  const reducedMotion = useReducedMotion();
  const [desktop, setDesktop] = useState(() => window.matchMedia("(min-width: 901px)").matches);
  const items = useMemo(() => feedFor(active), [active]);
  useEffect(() => {
    const media = window.matchMedia("(min-width: 901px)");
    const update = () => setDesktop(media.matches);
    media.addEventListener("change", update);
    return () => media.removeEventListener("change", update);
  }, []);
  const showReel = playing && !reducedMotion;
  const columnCount = desktop ? 3 : 2;
  return (
    <section id="gallery" className="section gallery" data-testid="gallery-section">
      <header className="section__head section__head--split">
        <div>
          <span className="overline">◆ The Index · Selected designs</span>
          <h2 className="section__title serif">All <span className="italic">work,</span> sorted.</h2>
          <p className="section__lede">Choose a category. Pause the reel to browse and open any original design.</p>
        </div>
        <div className="gallery__filters" role="group" aria-label="Filter work by category" data-testid="gallery-filters">
          {categories.map(category => (
            <button key={category} type="button" className={`chip ${active === category ? "chip--on" : ""}`}
              aria-pressed={active === category} onClick={() => setActive(category)}>
              {category} <span className="chip__count">{feedFor(category).length}</span>
            </button>
          ))}
        </div>
      </header>
      <div className="gallery__toolbar">
        <p role="status">{items.length} designs · {active}</p>
        {!reducedMotion && <button type="button" className="chip" aria-pressed={showReel}
          onClick={() => setPlaying(value => !value)}>{showReel ? "Pause reel and browse" : "Play reel"}</button>}
      </div>
      {showReel ? (
        <div className="reel" aria-hidden="true">
          <div className="reel__cols">
            {Array.from({ length: columnCount }, (_, column) => <ReelColumn key={`${active}-${column}`} items={items.filter((_, i) => i % columnCount === column)} direction={column % 2 ? "down" : "up"} />)}
          </div>
        </div>
      ) : (
        <div className="gallery-grid" data-testid="gallery-grid">
          {items.map(item => <figure className="reel-tile" key={item.src}>
            <a className="gallery-image-link" href={item.src} target="_blank" rel="noreferrer"
              aria-label={`Open original ${item.title} design ${item.index + 1} in a new tab`}>
              <PortfolioImage src={item.src} alt={`${item.title} — design ${item.index + 1}`} />
            </a>
            <figcaption className="reel-tile__cap"><span>{item.title}</span><span>{item.index + 1} / {items.length}</span></figcaption>
          </figure>)}
        </div>
      )}
    </section>
  );
}

function ReelColumn({ items, direction }) {
  const track = useRef(null);
  const [duration, setDuration] = useState(60);
  useEffect(() => {
    const observer = new ResizeObserver(() => setDuration(Math.max(30, track.current.scrollHeight / 2 / 22)));
    observer.observe(track.current);
    return () => observer.disconnect();
  }, []);
  return <div className="reel-col"><div ref={track} className={`reel-col__track reel-col__track--${direction}`} style={{ animationDuration: `${duration}s` }}>
    {[...items, ...items].map((item, i) => <figure key={`${item.src}-${i}`} className="reel-tile">
      <PortfolioImage src={item.src} alt="" />
    </figure>)}
  </div></div>;
}
